import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Separator } from '@vaiu/ui/separator';
import { 
  Search, 
  MapPin, 
  Store, 
  Users, 
  Gift, 
  Zap, 
  TrendingUp,
  Star,
  CheckCircle,
  ArrowRight,
  QrCode,
  CreditCard,
  Shield,
  Globe,
  MessageSquare,
  Trophy,
  Target,
  Sparkles,
  Heart,
  Building,
  Phone,
  Mail,
  ExternalLink,
  Play,
  Award,
  Coins,
  Share2,
  UserPlus,
  UserX,
  ChevronRight,
  AlertCircle,
  Lightbulb,
  Megaphone,
  HandHeart
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { i18n } from '@/utils/i18n';
// import VaiuLogo from '@/components/shared/VaiuLogo';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import InteractiveOnboarding from '@/components/InteractiveOnboarding';

interface MerchantSuggestion {
  id: string;
  name: string;
  category: string;
  location: string;
  requestCount: number;
  estimatedRevenue: number;
  distance?: string;
}

interface SuccessStory {
  id: string;
  merchantName: string;
  category: string;
  requestCount: number;
  timeToOnboard: string;
  advocateName: string;
  rewardEarned: number;
  image?: string;
}

interface NetworkStats {
  totalMerchants: number;
  totalAdvocates: number;
  totalRequests: number;
  successfulOnboardings: number;
  totalRewardsEarned: number;
}

interface HomePageProps {
  isGuestRestricted?: boolean;
  attemptedPath?: string;
}

const HomePage: React.FC<HomePageProps> = ({ isGuestRestricted = false, attemptedPath }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isGuest } = useAuth();
  const { toast } = useToast();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [merchantSuggestions, setMerchantSuggestions] = useState<MerchantSuggestion[]>([]);
  const [successStories, setSuccessStories] = useState<SuccessStory[]>([]);
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<'discover' | 'rewards' | 'community'>('discover');

  // Redirect authenticated non-guest users to dashboard
  useEffect(() => {
    if (!authLoading && user && !isGuest) {
      navigate('/');
    }
  }, [user, authLoading, navigate, isGuest]);

  // Load initial data
  useEffect(() => {
    loadHomePageData();
  }, []);

  const loadHomePageData = async () => {
    try {
      setLoading(true);
      
      // Load mock data for now - would be real API calls in production
      setMerchantSuggestions([
        {
          id: '1',
          name: 'Local Coffee Roasters',
          category: 'Food & Beverage',
          location: 'Downtown',
          requestCount: 47,
          estimatedRevenue: 2500,
          distance: '0.3 miles'
        },
        {
          id: '2',
          name: 'Green Garden Market',
          category: 'Grocery',
          location: 'Midtown',
          requestCount: 32,
          estimatedRevenue: 1800,
          distance: '0.7 miles'
        },
        {
          id: '3',
          name: 'Tech Repair Hub',
          category: 'Electronics',
          location: 'Tech District',
          requestCount: 28,
          estimatedRevenue: 3200,
          distance: '1.2 miles'
        }
      ]);

      setSuccessStories([
        {
          id: '1',
          merchantName: 'Artisan Bakery',
          category: 'Food & Beverage',
          requestCount: 65,
          timeToOnboard: '2 weeks',
          advocateName: 'Sarah M.',
          rewardEarned: 500
        },
        {
          id: '2',
          merchantName: 'Fitness First Gym',
          category: 'Health & Fitness',
          requestCount: 43,
          timeToOnboard: '1 week',
          advocateName: 'Mike T.',
          rewardEarned: 750
        }
      ]);

      setNetworkStats({
        totalMerchants: 1247,
        totalAdvocates: 8934,
        totalRequests: 15678,
        successfulOnboardings: 234,
        totalRewardsEarned: 125600
      });

    } catch (error) {
      console.error('Failed to load home page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMerchantRequest = async (merchant: { id: string; name: string; location?: string; category?: string }) => {
    if (!user) {
      // Store the intent and redirect to signup
      sessionStorage.setItem('pendingMerchantRequest', JSON.stringify(merchant));
      navigate('/signup?intent=merchant-request');
      return;
    }

    try {
      await platformApi.requestMerchant(merchant);
      toast({
        title: 'Request submitted!',
        description: 'We\'ll reach out to this merchant and notify you of their response.',
      });
    } catch (error) {
      toast({
        title: 'Request failed',
        description: 'Unable to submit merchant request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSearchMerchants = () => {
    if (!searchTerm.trim()) {
      toast({
        title: 'Enter merchant name',
        description: 'Please enter a merchant name to search.',
        variant: 'destructive',
      });
      return;
    }

    // Store search intent and redirect to signup
    sessionStorage.setItem('pendingMerchantSearch', JSON.stringify({
      term: searchTerm,
      location: location
    }));
    navigate('/signup?intent=merchant-search');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen page-background">
            {/* Subtle Guest Restriction Notice */}
      {isGuestRestricted && isGuest && (
        <div className="max-w-2xl mx-auto mt-4 mx-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                Some features require a full account. As a guest, you can use <strong>pay</strong> and <strong>redeem</strong> features.
              </p>
              <Button
                variant="link"
                className="text-blue-600 p-0 h-auto text-sm mt-1"
                onClick={() => navigate('/signup', { state: { fromGuest: true } })}
              >
                Upgrade to full account →
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Logo Row */}
      <div className="w-full py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-center">
            {/* <VaiuLogo size="xl" variant="wordmark" showTagline={true} /> */}
          </div>
        </div>
      </div>

      {/* Sign In/Join Row - Show different buttons for guests */}
      <div className="w-full pb-6">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-center">
            {isGuest ? (
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <UserX className="w-4 h-4" />
                  <span>You're browsing as <strong>{user?.username}</strong></span>
                  <Badge variant="secondary">Guest</Badge>
                </div>
                <Button 
                  size="lg" 
                  onClick={() => navigate('/signup', { state: { fromGuest: true, guestUsername: user?.username } })}
                  className="flex items-center space-x-2"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>{i18n.__('guest.learn_more_join') || 'Learn more and join'}</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <p className="text-xs text-gray-500 max-w-md text-center">
                  Create a free account to unlock all features and save your data permanently
                </p>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  Sign In
                </Button>
                <Button onClick={() => navigate('/signup')}>
                  Sign up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interactive Onboarding Section */}
      <section className="py-12">
        <InteractiveOnboarding />
      </section>

      {/* Network Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              {window.location.hostname.includes('atomicmarketplace.com')
                ? <>{i18n.__('home.pageTitle')} <span className="text-blue-600">Atomic Marketplace</span></>
                : <>{i18n.__('home.pageSubtitle')} <span className="text-blue-600">{i18n.__('home.makeMoney')}</span></>
              }
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {window.location.hostname.includes('atomicmarketplace.com')
                ? i18n.__('home.atomicSubtitle')
                : i18n.__('home.vaiuSubtitle')
              }
            </p>
          </div>

          {/* Network Stats */}
          {networkStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{networkStats.totalMerchants.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Merchants</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{networkStats.totalAdvocates.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Advocates</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{networkStats.totalRequests.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Requests</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">${(networkStats.totalRewardsEarned / 100).toLocaleString()}</div>
                <div className="text-sm text-gray-600">Rewards Earned</div>
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/signup')} className="text-lg px-8 py-3">
              <UserPlus className="w-5 h-5 mr-2" />
              {i18n.__('home.startAdvocating')}
            </Button>
            <Button size="lg" variant="outline" onClick={() => setActiveSection('discover')} className="text-lg px-8 py-3">
              <Search className="w-5 h-5 mr-2" />
              {i18n.__('home.discoverMerchants')}
            </Button>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center space-x-1 bg-white/80 backdrop-blur-sm rounded-lg p-1">
            {[
              { id: 'discover', label: 'Discover Merchants', icon: Search },
              { id: 'rewards', label: 'Earn Rewards', icon: Gift },
              { id: 'community', label: 'Join Community', icon: Users }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as any)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-md font-medium transition-colors ${
                  activeSection === tab.id
                    ? 'bg-gray-700 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Content Sections */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-6xl mx-auto">
          
          {/* Merchant Discovery Section */}
          {activeSection === 'discover' && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">Request Your Favorite Merchants</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Can't pay with Vaiu at your favorite store? Request them to join and earn rewards when they do!
                </p>
              </div>

              {/* Search Interface */}
              <Card className="max-w-2xl mx-auto">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Merchant Name</label>
                      <Input
                        placeholder="e.g., Starbucks, Target, Local Coffee Shop..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="text-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Location (Optional)</label>
                      <Input
                        placeholder="e.g., Downtown Seattle, 5th Avenue..."
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      />
                    </div>
                    <Button onClick={handleSearchMerchants} className="w-full" size="lg">
                      <Search className="w-5 h-5 mr-2" />
                      Request This Merchant
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Popular Merchant Requests */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-center">Popular Requests in Your Area</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {merchantSuggestions.map((merchant) => (
                    <Card key={merchant.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-lg">{merchant.name}</h4>
                              <p className="text-sm text-gray-600">{merchant.category}</p>
                              <p className="text-xs text-gray-500 flex items-center mt-1">
                                <MapPin className="w-3 h-3 mr-1" />
                                {merchant.location} • {merchant.distance}
                              </p>
                            </div>
                            <Badge variant="secondary">{merchant.requestCount} requests</Badge>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Potential monthly revenue:</span>
                              <span className="font-medium">${merchant.estimatedRevenue.toLocaleString()}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full" 
                                style={{ width: `${Math.min((merchant.requestCount / 50) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500">
                              {50 - merchant.requestCount} more requests needed for outreach
                            </p>
                          </div>

                          <Button 
                            onClick={() => handleMerchantRequest(merchant)}
                            className="w-full"
                            variant="outline"
                          >
                            <HandHeart className="w-4 h-4 mr-2" />
                            Add My Request
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Rewards Section */}
          {activeSection === 'rewards' && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">Earn Meaningful Rewards</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Get rewarded for every contribution you make to growing the network. Your advocacy pays off!
                </p>
              </div>

              {/* Reward Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: 'Merchant Requests',
                    description: 'Earn points for each merchant you request',
                    points: '10-50 points',
                    icon: Store,
                    color: 'bg-blue-100 text-blue-600'
                  },
                  {
                    title: 'Successful Onboarding',
                    description: 'Bonus when your requested merchant joins',
                    points: '500-2000 points',
                    icon: CheckCircle,
                    color: 'bg-green-100 text-green-600'
                  },
                  {
                    title: 'Referral Rewards',
                    description: 'Bring friends to the network',
                    points: '200 points',
                    icon: UserPlus,
                    color: 'bg-purple-100 text-purple-600'
                  },
                  {
                    title: 'Community Engagement',
                    description: 'Social sharing and participation',
                    points: '5-25 points',
                    icon: MessageSquare,
                    color: 'bg-orange-100 text-orange-600'
                  }
                ].map((reward, index) => (
                  <Card key={index} className="text-center">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-full ${reward.color} flex items-center justify-center mx-auto mb-4`}>
                        <reward.icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-semibold mb-2">{reward.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{reward.description}</p>
                      <Badge variant="outline" className="font-bold">{reward.points}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Redemption Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-center">Redeem Your Points</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { label: 'Cash Back', icon: Coins, desc: 'Direct to your account' },
                      { label: 'Vaiu Credits', icon: QrCode, desc: 'For transactions' },
                      { label: 'Merchant Discounts', icon: Store, desc: 'Exclusive offers' },
                      { label: 'Premium Features', icon: Star, desc: 'Enhanced benefits' },
                      { label: 'Charity Donations', icon: Heart, desc: 'Give back' }
                    ].map((option, index) => (
                      <div key={index} className="text-center p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                        <option.icon className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-gray-500">{option.desc}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Success Stories */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-center">Recent Success Stories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {successStories.map((story) => (
                    <Card key={story.id} className="bg-gradient-to-r from-green-50 to-blue-50">
                      <CardContent className="p-6">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">{story.merchantName}</h4>
                            <Badge className="bg-green-600">{story.category}</Badge>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>• {story.requestCount} community requests</p>
                            <p>• Onboarded in {story.timeToOnboard}</p>
                            <p>• Advocate: {story.advocateName}</p>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t">
                            <span className="text-sm font-medium">Reward Earned:</span>
                            <span className="text-lg font-bold text-green-600">{story.rewardEarned} points</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Community Section */}
          {activeSection === 'community' && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">Join the Community</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Connect with other advocates, share strategies, and work together to grow the network in your area.
                </p>
              </div>

              {/* Community Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span>Local Communities</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">Join advocates in your city and neighborhood to coordinate merchant requests.</p>
                    <div className="space-y-2">
                      {['Seattle Advocates (234 members)', 'Downtown District (89 members)', 'University Area (156 members)'].map((community, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <span className="text-sm">{community}</span>
                          <Button size="sm" variant="outline">Join</Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Trophy className="w-5 h-5 text-yellow-600" />
                      <span>Leaderboards</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">See top advocates and compete in friendly challenges.</p>
                    <div className="space-y-2">
                      {[
                        { name: 'Alex M.', points: 2450, rank: 1 },
                        { name: 'Sarah K.', points: 2180, rank: 2 },
                        { name: 'Mike R.', points: 1950, rank: 3 }
                      ].map((advocate) => (
                        <div key={advocate.rank} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              advocate.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                              advocate.rank === 2 ? 'bg-gray-100 text-gray-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {advocate.rank}
                            </div>
                            <span className="font-medium">{advocate.name}</span>
                          </div>
                          <span className="text-sm text-gray-600">{advocate.points.toLocaleString()} pts</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Share2 className="w-5 h-5 text-green-600" />
                      <span>Social Advocacy</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">Share merchant requests on social media to amplify your impact.</p>
                    <div className="grid grid-cols-3 gap-3">
                      {['Twitter', 'Facebook', 'Instagram'].map((platform) => (
                        <Button key={platform} variant="outline" size="sm" className="text-xs">
                          {platform}
                        </Button>
                      ))}
                    </div>
                    <Alert>
                      <Lightbulb className="w-4 h-4" />
                      <AlertDescription>
                        Earn bonus points for social shares that lead to new advocate signups!
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      <span>Group Challenges</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">Work together on time-limited campaigns with bonus rewards.</p>
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg bg-purple-50">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium">Coffee Shop Challenge</h4>
                          <Badge variant="secondary">5 days left</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Request 10 local coffee shops</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-purple-600 h-2 rounded-full" style={{ width: '70%' }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">7/10 completed • 2x bonus points</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-gray-700 to-gray-800 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            {window.location.hostname.includes('atomicmarketplace.com')
              ? 'Start accepting payments in seconds'
              : 'Join the tokenized commerce economy'
            }
          </h2>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {window.location.hostname.includes('atomicmarketplace.com')
              ? 'Set up your merchant account instantly. No website needed, no complexity, just instant commerce.'
              : 'Join thousands of advocates who are building the payment ecosystem they want to use. Your voice matters, and your advocacy pays off.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {window.location.hostname.includes('atomicmarketplace.com') ? (
              <Button size="lg" onClick={() => navigate('/signup?flow=merchant')} className="bg-white text-gray-800 hover:bg-gray-100 text-lg px-8 py-3">
                <Sparkles className="w-5 h-5 mr-2" />
                Become a Merchant
              </Button>
            ) : (
              <>
                <Button size="lg" onClick={() => navigate('/signup')} className="bg-white text-gray-800 hover:bg-gray-100 text-lg px-8 py-3">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Earning Rewards
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/login')} className="border-white text-white hover:bg-white hover:text-gray-800 text-lg px-8 py-3">
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Sign In
                </Button>
              </>
            )}
          </div>
          {window.location.hostname.includes('atomicmarketplace.com') && (
            <p className="text-sm text-gray-400 mt-4">
              Free to start • No processing fees • $250 annual membership fee due after your 100th sale
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              {/* <VaiuLogo size="md" variant="wordmark" className="text-white" /> */}
              <p className="text-gray-400 text-sm">
                Building the future of payments through community-driven growth.
              </p>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold">For Advocates</h3>
              <div className="space-y-2 text-sm">
                <a href="#" className="text-gray-400 hover:text-white block">How It Works</a>
                <a href="#" className="text-gray-400 hover:text-white block">Rewards Program</a>
                <a href="#" className="text-gray-400 hover:text-white block">Community Guidelines</a>
                <a href="#" className="text-gray-400 hover:text-white block">Success Stories</a>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold">For Merchants</h3>
              <div className="space-y-2 text-sm">
                <a href="#" className="text-gray-400 hover:text-white block">Sign up</a>
                <a href="#" className="text-gray-400 hover:text-white block">Business Benefits</a>
                <a href="#" className="text-gray-400 hover:text-white block">Integration Guide</a>
                <a href="#" className="text-gray-400 hover:text-white block">Support</a>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold">Company</h3>
              <div className="space-y-2 text-sm">
                <a href="/help" className="text-gray-400 hover:text-white block">Help Center</a>
                <a href="/privacy-policy" className="text-gray-400 hover:text-white block">Privacy Policy</a>
                <a href="/terms-of-service" className="text-gray-400 hover:text-white block">Terms of Service</a>
                <a href="#" className="text-gray-400 hover:text-white block">Contact Us</a>
              </div>
            </div>
          </div>
          
          <Separator className="my-8 bg-gray-700" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Vaiu. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white">
                <span className="sr-only">Twitter</span>
                <MessageSquare className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white">
                <span className="sr-only">LinkedIn</span>
                <Building className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white">
                <span className="sr-only">GitHub</span>
                <Globe className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 